import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {Store} from '@ngrx/store';
import {filter, first, switchMap, takeUntil} from 'rxjs/operators';
import {Subject} from "rxjs";
import * as fromStore from "../../../store";
import {FormControl, FormGroup} from "@angular/forms";
import {IFcDropdownSelectOption} from "../../../shared/components/form-controls/dropdown-select/fc-dropdown-select-option";
import {DbDateParserFormatter} from "../../../modules/datepicker/ngb-date-parser-formatter";
import {TypeUtils} from "../../../shared/utils/type-utils";
import {Website} from "../../../websites/website/website";
import actionsDropdownList from "./actions-dropdown-list.json";
import {ActivatedRoute, Router} from "@angular/router";
import {DatePipe, Location} from '@angular/common';
import {Utils} from "../../../shared/utils/utils";

@Component({
    selector: 'db-activity-log-filters',
    templateUrl: './filters.component.html',
    styleUrls: ['./filters.component.scss'],
    standalone: false
})
export class ActivityLogFiltersComponent implements OnInit, OnDestroy {

  @Input() clientsList: any;
  public filtersForm: FormGroup;
  public startDate: any;
  public endDate: any;
  public displayStartDate: any;
  public displayEndDate: any;
  public defaultValues: any;
  public firstNotNetworkWebsite: Website;
  public firstNetworkWebsite: Website;
  public chosenWebsiteId: number;
  public actionsList: Array<IFcDropdownSelectOption> = actionsDropdownList;
  public hasStagingEnv: boolean;
  public websiteEnvTypes: Array<IFcDropdownSelectOption>;
  public websites: Array<IFcDropdownSelectOption> = [
    {
      "label": 'All Websites',
      "value": 'all'
    }
  ];

  @Output() onFilter = new EventEmitter<any>();
  private destroyStream: Subject<void> = new Subject();

  constructor(
    private _parseFormatter: DbDateParserFormatter,
    private _store: Store<fromStore.DbStoreState>,
    private _activeRoute: ActivatedRoute,
    private _router: Router,
    private _location: Location,
    private _datePipe: DatePipe
  ) {}

  ngOnInit() {
    this.defaultValues = (<any>this._activeRoute.queryParams).value;
    const client = TypeUtils.isDefined(this.defaultValues.userEmail) ? this.defaultValues.userEmail : "";
    const action = TypeUtils.isDefined(this.defaultValues.type) ? this.defaultValues.type : "";
    const websiteType = TypeUtils.isDefined(this.defaultValues.websiteType) ? this.defaultValues.websiteType : "";
    const startDate = TypeUtils.isDefined(this.defaultValues.startDate) ? new Date(parseInt(this.defaultValues.startDate, 10)) : null;
    const endDate = TypeUtils.isDefined(this.defaultValues.endDate) ? new Date(parseInt(this.defaultValues.endDate, 10)) : null;
    if (startDate && endDate) {
      this.displayStartDate = { day: startDate.getDate(), month: startDate.getMonth() + 1, year: startDate.getFullYear()};
      this.displayEndDate = { day: endDate.getDate(), month: endDate.getMonth() + 1, year: endDate.getFullYear()};
    }
    this.chosenWebsiteId = TypeUtils.isDefined(this.defaultValues.websiteId) ? parseInt(this.defaultValues.websiteId, 10) : null;

    this.filtersForm = new FormGroup({
      'filterByClient': new FormControl(client),
      'filterByAction': new FormControl(action),
      'filterByWebsite': new FormControl(this.chosenWebsiteId),
      'filterByWebsiteType': new FormControl(websiteType),
    });
    this.addEventHandlers();
  }

  private addEventHandlers(): void {
    this._store
      .select(fromStore.getWebsitesLoaded)
      .pipe(
        takeUntil(this.destroyStream),
        filter((loaded: boolean): boolean => !!loaded),
        switchMap(() => this._store.select(fromStore.getWebsites))
      )
      .subscribe((websites: Website[]) => {
        websites = websites.filter(website => website.type === 'live' && !website.parentId);
        const websiteList = websites.sort((website1: Website, website2: Website) => {
          if (website1 && website2) {
            if (!website1.isNetwork && website2.isNetwork) {
              return 1;
            } else if (website1.isNetwork && !website2.isNetwork) {
              return -1;
            }
          }
          return 0;
        });
        this.firstNotNetworkWebsite = websiteList.find(website => !website.isNetwork);
        this.firstNetworkWebsite = websiteList.find(website => !!website.isNetwork);
        Object.keys(websiteList).forEach((key: string): void => {
          this.websites.push({
            "label": Utils.replaceUrl(websiteList[key].site_url),
            "value": websiteList[key].id,
            "isAvailable": websiteList[key].siblingId,
            "optionTitle": TypeUtils.isDefined(this.firstNetworkWebsite) && TypeUtils.isDefined(this.firstNotNetworkWebsite)
              ? ((this.firstNetworkWebsite.id === websiteList[key].id ? 'Multisite network'
                   : (TypeUtils.isDefined(this.firstNotNetworkWebsite) && this.firstNotNetworkWebsite.id === websiteList[key].id ? 'Websites' : false)))
              : false
          });
        });
        if (this.chosenWebsiteId) {
          this.filtersForm.controls['filterByWebsite'].setValue(this.chosenWebsiteId);
        }
        const findWebsite = websiteList.find(item => item.id === this.filtersForm.controls['filterByWebsite'].value);
        this.hasStagingEnv = findWebsite && findWebsite.siblingId && !findWebsite.isNetwork;
        if (this.filtersForm.controls['filterByWebsite'].value) {
          if (this.hasStagingEnv && findWebsite.siblingId && findWebsite.siblingId.dev) {
            this._store.select(fromStore.getWebsite(findWebsite.siblingId.dev))
              .pipe(first())
              .subscribe((devWebsite: Website): void => {
                this.websiteEnvTypes = this.defineWebsiteEnv(Utils.replaceUrl(findWebsite.site_url), Utils.replaceUrl(devWebsite.site_url));
              });
          }
          else {
            this.websiteEnvTypes = this.defineWebsiteEnv(Utils.replaceUrl(findWebsite.site_url), Utils.replaceUrl(findWebsite.site_url));
          }
        }
      });

    this.filtersForm.controls['filterByAction'].valueChanges.subscribe((item): void => {
      this.filtersForm.controls['filterByWebsite'].setValue('');
      this.filtersForm.controls['filterByWebsiteType'].setValue('');
    });

    this.filtersForm.controls['filterByWebsite'].valueChanges.subscribe((item): void => {
      this.hasStagingEnv = this.findWebsite(item) && !!TypeUtils.isObject(this.findWebsite(item).isAvailable) && this.findWebsite(item).optionTitle !== 'Multisite network';
    });
  }

  private findWebsite(item): any {
    if (TypeUtils.isDefined(this.defaultValues.type) && this.defaultValues.type === 'website') {
      return this.websites.find((website) => website.value == item);
    }
    return null;
  }

  ngOnDestroy(): void {
    this.destroyStream.next();
  }

  public filterActivities(): void {
    const data = {
      "userEmail": this.filtersForm.controls['filterByClient'].value || "all",
      "type": this.filtersForm.controls['filterByAction'].value || "all",
      "websiteId": this.filtersForm.controls['filterByWebsite'].value || "all",
      "websiteType": this.filtersForm.controls['filterByWebsiteType'].value || "all",
      "startDate": this.startDate || null,
      "endDate": this.endDate || null
    };
    const tempData = [];
    if (this.filtersForm.controls['filterByWebsite'].value) {
      const findWebsite = this.websites.find((website) => website.value == this.filtersForm.controls['filterByWebsite'].value);
      this.hasStagingEnv = !!TypeUtils.isObject(findWebsite.isAvailable) && findWebsite.optionTitle !== 'Multisite network';
      if (this.hasStagingEnv && findWebsite && findWebsite.isAvailable.dev) {
        this._store.select(fromStore.getWebsite(findWebsite.isAvailable.dev))
          .pipe(first())
          .subscribe((devWebsite: Website): void => {
            this.websiteEnvTypes = this.defineWebsiteEnv(Utils.replaceUrl(findWebsite.label), Utils.replaceUrl(devWebsite.site_url));
          });
      }
      else {
        this.defineWebsiteEnv(Utils.replaceUrl(findWebsite.label), Utils.replaceUrl(findWebsite.label))
      }
    }
    for (const member in data) {
      if ( TypeUtils.isDefined(data[member]) && !TypeUtils.isNull(data[member])
        && data[member] !== "" && data[member] !== "all" ) {
        tempData[member] = data[member];
      }
    }
    this.changeUrl(tempData);
    this.onFilter.emit(tempData);
  }

  public changeUrl(tempData): void {
    const urlTree = this._router.createUrlTree([], {
      queryParams: tempData,
      relativeTo: this._activeRoute,
      // skipLocationChange: true
    });
    this._location.replaceState(urlTree.toString());
  }

  public defineWebsiteEnv(siteUrl, devSiteUrl) {
    return [
      {
        value: 'live',
        label: 'Live Environment',
        description: siteUrl,
      },
      {
        value: 'staging',
        label: 'Staging Environment',
        description: devSiteUrl,
      },
    ];
  }

  public clearDateFilter(reset = false): void {
    this.startDate = '';
    this.endDate = '';
    this.displayStartDate = '';
    this.displayEndDate = '';
    if (reset) {
      this.filtersForm.reset();
    }
    this.filterActivities();
  }

  public dateFilter(e): void {
    const [start, end] = e;
    const startDate = this._parseFormatter.format(start) + ' 00:00:00';
    this.startDate = (new Date(startDate).getTime());
    this.displayStartDate = start;
    if (end) {
      const endDate = this._parseFormatter.format(end) + ' 23:59:59';
      this.endDate = (new Date(endDate).getTime());
      this.displayEndDate = end;
    }
    else {
      this.endDate = new Date().getTime();
    }
    this.filterActivities();
  }
}
